import {Component, HostListener, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {FlatTreeControl} from '@angular/cdk/tree';
import {MatTreeFlatDataSource, MatTreeFlattener} from '@angular/material/tree';
import {Hierarchy, HierarchyEntity, HierarchyService} from '../../shared/hierarchy.service';
import {first, map, share, takeUntil} from 'rxjs/operators';
import {combineLatest, of, Subject} from 'rxjs';
import {Router} from '@angular/router';
import {SmagApiService} from '../../api/smag-api.service';
import {MatChipInputEvent} from '@angular/material/chips';
import {MatDialog} from '@angular/material/dialog';
import {AsyncValidatorFn, FormControl, Validators} from '@angular/forms';
import {validateNotBlank} from '../../commons/validators';
import {ENTER} from '@angular/cdk/keycodes';
import * as _ from 'lodash';
import {CdkDragMove, CdkDragStart, DragRef} from '@angular/cdk/drag-drop';
import {MenuService, TreeMode} from '../../shared/menu.service';
import {StorageService} from '../../commons/storage.service';
import {can} from '../../shared/permissions';
import {Permission} from 'smag-commons/dto/api';
import {
    ENTITY_NAME_FORBIDDEN_CHARACTERS,
    ENTITY_NAME_MAX_LENGTH,
    ENTITY_NAME_VALIDATION_PATTERN,
    entityNameError
} from '../../shared/entity-names';
import {EntityType} from 'smag-commons/cfg/hierarchy';

const RENAME_DIALOG_HEIGHT = 160;
const TAG_DIALOG_HEIGHT = 175;
const DEFAULT_MAX_LEVEL = 1;
const TREE_SETTINGS_KEY = 'tree';

// flat entity node with expandable and level information
interface FlatEntityNode {
    expandable: boolean;
    level: number;
    entity: HierarchyEntity;
}

interface TreeSettings {
    expansionMap: { [entityId: string]: boolean };
}

// used while moving tree nodes
interface NodeDragInfo {
    draggedEntity: HierarchyEntity;
    intersectedEntity: HierarchyEntity;
    canceledByEsc: boolean;
    dragReset: () => void;
}

@Component({
    selector: 'app-entity-tree',
    templateUrl: './entity-tree.component.html',
    styleUrls: ['./entity-tree.component.scss']
})
export class EntityTreeComponent implements OnInit, OnDestroy {

    private destroyed = new Subject<void>();
    private settings: TreeSettings;
    @ViewChild('entityRename') private entityRenameDialog: TemplateRef<any>;
    @ViewChild('entityTag') private entityTagDialog: TemplateRef<any>;
    @ViewChild('entityMove') private moveEntityDialog: TemplateRef<any>;

    mode: TreeMode;
    hierarchy: Hierarchy;
    selection: HierarchyEntity;
    dragInfo: NodeDragInfo; // current dragging information
    treeControl: FlatTreeControl<FlatEntityNode>;
    treeFlattener: MatTreeFlattener<HierarchyEntity, FlatEntityNode>;
    dataSource: MatTreeFlatDataSource<HierarchyEntity, FlatEntityNode>;
    searchText: string;
    hasChild = (num: number, node: FlatEntityNode) => node.expandable;

    constructor(private readonly router: Router,
                private readonly menuService: MenuService,
                private readonly apiService: SmagApiService,
                private readonly hierarchyService: HierarchyService,
                private readonly storageService: StorageService,
                private readonly dialog: MatDialog) {
        this.settings = null;
        this.mode = null;
        this.hierarchy = null;
        this.selection = null;
        this.dragInfo = null;
        this.treeControl = new FlatTreeControl<FlatEntityNode>(node => node.level, node => node.expandable);
        this.treeFlattener = new MatTreeFlattener(this.transformer,
            node => node.level, node => node.expandable, node => node.children || []);
        this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
        this.searchText = '';
    }

    ngOnInit() {
        combineLatest([
            this.hierarchyService.hierarchy,
            this.menuService.treeMode
        ]).pipe(
            takeUntil(this.destroyed)
        ).subscribe(([hierarchy, mode]) => {
            this.hierarchy = hierarchy;
            this.mode = mode;
            this.dataSource.data = this.mkTreeData();
            this.dataSource._flattenedData.pipe(
                share(),
                first()
            ).subscribe(d => {
                this.treeControl.dataNodes = d;
                // this.treeControl.expandAll();
                this.loadSyncSettings(d);
                d.forEach(node => {
                    if (this.settings.expansionMap[node.entity.id]) {
                        this.treeControl.expand(node);
                    }
                });
            });
        });

        this.hierarchyService.selection.pipe(
            takeUntil(this.destroyed)
        ).subscribe(e => this.selection = e);

        // NOTE: selection is assumed to be a reference to an actual entity within the hierarchy
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    sameEntity(e1: HierarchyEntity, e2: HierarchyEntity): boolean {
        // NOTE: entity equality is checked through equality of ids,
        //       as entity reference is not assumed to be reliable (for the sake of robustness...)
        if (!e1 || !e2) {
            return false;
        }
        return e1.id === e2.id;
    }

    searchMatches(node: FlatEntityNode): boolean {
        const searchText = this.searchText ? this.searchText.trim() : '';
        return !searchText.length || searchForName(node.entity, searchText);
    }

    canBeSelected(node: FlatEntityNode): boolean {
        return true;
    }

    isEditable(node: FlatEntityNode): boolean {
        return this.mode === TreeMode.EDIT && this.sameEntity(node.entity, this.selection);
    }

    isDraggable(node: FlatEntityNode): boolean {
        return this.mode === TreeMode.EDIT && can(node.entity.permissions, Permission.DELETE);
    }

    canBeMovedToRoot(node: FlatEntityNode): boolean {
        if (!node.entity.parent) {
            return false; // already at root level
        }
        return can(node.entity.permissions, Permission.DELETE) &&
            can(this.hierarchy.permissions, Permission.CREATE); // hierarchy's permissions implicitly refer to its root
    }

    canBeSorted(node: FlatEntityNode, sorting: 'up' | 'down'): boolean {
        if (!can(node.entity.permissions, Permission.DELETE) ||
            !can(!!node.entity.parent ? node.entity.parent.permissions : this.hierarchy.permissions, Permission.CREATE)) {
            return false;
        }
        const [siblings, nodeIndex] = this.findEntityWithinSiblings(node.entity);
        return sorting === 'up' ? nodeIndex > 0 : nodeIndex < siblings.length - 1;
    }

    canBeRenamed(node: FlatEntityNode): boolean {
        return can(node.entity.permissions, Permission.UPDATE);
    }

    canBeTagged(node: FlatEntityNode): boolean {
        return can(node.entity.permissions, Permission.UPDATE);
    }

    onToggleExpansion(node: FlatEntityNode, expanded: boolean) {
        this.settings.expansionMap[node.entity.id] = expanded;
        this.saveSettings();
    }

    onClickClearSearchText() {
        this.searchText = '';
    }

    onClickMoveToRoot(node: FlatEntityNode) {
        this.moveEntity(node.entity, null);
    }

    onClickSort(node: FlatEntityNode, sorting: 'up' | 'down') {
        const [siblings, nodeIndex] = this.findEntityWithinSiblings(node.entity);
        if (sorting === 'up') {
            const beforePos = nodeIndex - 1;
            this.sortEntity(node.entity, siblings[beforePos].id, null);
        } else {
            const afterPos = nodeIndex + 1;
            this.sortEntity(node.entity, null, siblings[afterPos].id);
        }
    }

    onClickRename(event: MouseEvent, node: FlatEntityNode) {
        const nameCtrl = new FormControl(node.entity.name,
            [validateNotBlank, Validators.pattern(ENTITY_NAME_VALIDATION_PATTERN)],
            [this.mkNameUniquenessValidator(node.entity)]);
        this.dialog.open(this.entityRenameDialog, {
            data: {
                entity: node.entity,
                nameFormControl: nameCtrl,
                forbiddenChars: ENTITY_NAME_FORBIDDEN_CHARACTERS.join(', '),
                nameError: () => entityNameError(nameCtrl),
                maxLength: ENTITY_NAME_MAX_LENGTH
            },
            minHeight: `${RENAME_DIALOG_HEIGHT}px`,
            position: {
                left: `${event.clientX}px`,
                top: event.clientY + RENAME_DIALOG_HEIGHT > document.documentElement.clientHeight ?
                    `${event.clientY - RENAME_DIALOG_HEIGHT}px` :
                    `${event.clientY}px`
            },
            hasBackdrop: true
        }).afterClosed().subscribe(newName => {
            // check if we are actually changing name
            if (newName && newName !== node.entity.name) {
                this.renameEntity(node.entity, newName);
            }
        });
    }

    onClickTag(event: MouseEvent, node: FlatEntityNode) {
        this.dialog.open(this.entityTagDialog, {
            data: {
                entity: node.entity,
                tags: [...(node.entity.tags || [])],
                separatorKeysCodes: [ENTER],
                addTag: (ev: MatChipInputEvent, tags: string[]) => {
                    const input = ev.input;
                    const value = (ev.value || '').trim();
                    if (value.length && tags.indexOf(value) < 0) {
                        tags.push(value);
                        tags.sort();
                    }
                    if (input) {
                        input.value = '';
                    }
                },
                removeTag: (tag: string, tags: string[]) => {
                    const index = tags.indexOf(tag);
                    if (index >= 0) {
                        tags.splice(index, 1);
                    }
                }
            },
            minHeight: `${TAG_DIALOG_HEIGHT}px`,
            position: {
                left: `${event.clientX}px`,
                top: event.clientY + TAG_DIALOG_HEIGHT > document.documentElement.clientHeight ?
                    `${event.clientY - TAG_DIALOG_HEIGHT}px` :
                    `${event.clientY}px`
            },
            hasBackdrop: true
        }).afterClosed().subscribe(tags => {
            if (tags) {
                const currTags = _.sortBy(node.entity.tags || [], t => t);
                const newTags = _.sortBy(tags, t => t);
                // check if we are actually changing tags
                if (!_.isEqual(currTags, newTags)) {
                    this.tagEntity(node.entity, newTags);
                }
            }
        });
    }

    onStartDragNode(event: CdkDragStart, node: FlatEntityNode) {
        const expanded = this.treeControl.isExpanded(node);
        if (expanded) {
            this.treeControl.collapse(node);
        }
        this.dragInfo = {
            draggedEntity: node.entity,
            intersectedEntity: null,
            canceledByEsc: false,
            dragReset: () => this.resetDrag(node, expanded, event.source._dragRef)
        };
    }

    onEndDragNode() {
        if (this.dragInfo) {
            if (this.dragInfo.intersectedEntity && !this.dragInfo.canceledByEsc) {
                if (this.dragInfo.draggedEntity.parent?.id !== this.dragInfo.intersectedEntity?.id) {
                    this.moveEntity(this.dragInfo.draggedEntity, this.dragInfo.intersectedEntity);
                }
            }
            this.dragInfo.dragReset();
        }
    }

    onMoveDragNode(event: CdkDragMove) {
        if (!this.dragInfo) {
            return;
        }
        const treeNodes = document.getElementsByClassName('draggable-tree-node');
        const insersectedNode = this.findIntersection(treeNodes, event.source.element.nativeElement);
        this.dragInfo.intersectedEntity = !!insersectedNode && this.canBeDragTarget(insersectedNode.entity) ? insersectedNode.entity : null;
    }

    onSelectEntity(entity: HierarchyEntity) {
        this.hierarchyService.select(entity?.id);
    }

    @HostListener('window:keyup', ['$event'])
    onKeyUp(event: KeyboardEvent) {
        if (this.dragInfo && event.key === 'Escape') {
            this.dragInfo.canceledByEsc = true;
            document.dispatchEvent(new Event('mouseup'));
        }
    }

    private findIntersection(nodes: HTMLCollectionOf<Element>, draggingNode: Element): FlatEntityNode {
        const rect = draggingNode.getBoundingClientRect();
        // nodes returned by getElementsByClassName() are already (implicitly) "sorted" by y-position:
        // binary search is thus feasible
        // TODO: implement binary search to avoid linear scan of all nodes, but *beware* of dragging node!
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < nodes.length; ++i) {
            const nodeId = nodes[i].getAttribute('data-nodeId');
            const treeNode = this.treeControl.dataNodes.find(node => node.entity.id === nodeId);
            if (rectIntersect(rect, nodes[i].getBoundingClientRect()) && nodeId !== this.dragInfo.draggedEntity.id) {
                return treeNode;
            }
        }
        return null;
    }

    private canBeDragTarget(entity: HierarchyEntity): boolean {
        return can(entity.permissions, Permission.CREATE);
    }

    private renameEntity(entity: HierarchyEntity, newName: string) {
        this.apiService.renameEntity(entity.id, newName)
            .then(() => this.hierarchyService.reloadHierarchy());
    }

    private tagEntity(entity: HierarchyEntity, newTags: string[]) {
        this.apiService.tagEntity(entity.id, newTags).then(() => this.hierarchyService.reloadHierarchy());
    }

    private resetDrag(draggedNode: FlatEntityNode, initiallyExpanded: boolean, dragRef: DragRef) {
        if (initiallyExpanded) {
            this.treeControl.expand(draggedNode);
        }
        dragRef.reset();
        this.dragInfo = null;
    }

    private moveEntity(entity: HierarchyEntity, moveUnder: HierarchyEntity) {
        this.dialog.open(this.moveEntityDialog, {
            hasBackdrop: true,
            data: { // bound to $implicit
                entity,
                moveUnder,
                moveUnderRoot: !moveUnder
            }
        }).afterClosed().subscribe(move => {
            if (move) {
                this.apiService.moveEntity(entity.id, moveUnder ? moveUnder.id : this.hierarchy.root)
                    .then(() => this.hierarchyService.reloadHierarchy());
            }
        });
    }

    private sortEntity(entity: HierarchyEntity, moveBefore?: string, moveAfter?: string) {
        this.apiService.moveEntity(entity.id,
            entity.parent ? entity.parent.id : this.hierarchy.root, // keep current parent
            moveBefore,
            moveAfter).then(() => this.hierarchyService.reloadHierarchy());
    }

    private findEntityWithinSiblings(entity: HierarchyEntity): [HierarchyEntity[], number] {
        const siblings: HierarchyEntity[] = (entity.parent ? entity.parent.children : this.hierarchy.entities) || [];
        // NOTE: we do not rely on reference equality
        const entityIndex = siblings.findIndex(s => s.id === entity.id);
        return [siblings, entityIndex];
    }

    private loadSyncSettings(nodes: FlatEntityNode[]) {
        this.loadSettings();
        const expansionMap: { [entityId: string]: boolean } = {};
        // merge current settings with default settings for so-far unknown entities
        nodes.forEach(node => {
            const id = node.entity.id;
            expansionMap[id] = id in this.settings.expansionMap ?
                this.settings.expansionMap[id] :
                node.level < DEFAULT_MAX_LEVEL; // by default only nodes of level < `DEFAULT_MAX_LEVEL` are expanded
        });
        this.settings.expansionMap = expansionMap;
        // write new settings to storage
        this.saveSettings();
    }

    private loadSettings() {
        // in case settings are not found, default ones are used
        this.settings = this.storageService.loadLocal(TREE_SETTINGS_KEY) || {
            expansionMap: {}
        };
    }

    private saveSettings() {
        this.storageService.storeLocal(TREE_SETTINGS_KEY, this.settings);
    }

    private mkNameUniquenessValidator(entity: HierarchyEntity): AsyncValidatorFn {
        return ctrl => {
            const name = ctrl.value ? ctrl.value.trim() : null;
            if (!name || name === entity.name) {
                return of(null);
            }
            const siblings = entity.parent ?
                this.hierarchyService.findEntityById(entity.parent.id).pipe(
                    map(e => e.children || [])
                ) :
                this.hierarchyService.hierarchy.pipe(
                    map(hierarchy => hierarchy.entities || [])
                );
            return siblings.pipe(
                first(),
                map(entities => !!entities.find(sibling => sibling.name === name)),
                map(found => found ? {duplicate: true} : null)
            );
        };
    }

    private mkTreeData(): HierarchyEntity[] {
        switch (this.mode) {
            case TreeMode.ENERGY_COMMUNITY: {
                return mkEnergyCommunityTreeData(this.hierarchy.entities || []);
            }
            case TreeMode.EDIT:
                return this.hierarchy.entities || [];
        }
    }

    private transformer = (node: HierarchyEntity, level: number): FlatEntityNode => {
        return {
            expandable: !!node.children && node.children.length > 0,
            level,
            entity: node
        };
    }
}

// returns `true` if two rectangles (r1 and r2) overlap
function rectIntersect(r1: ClientRect | DOMRect, r2: ClientRect | DOMRect): boolean {
    // if one rectangle is on left side of other
    if (r1.left >= (r2.left + r2.width) || r2.left >= (r1.left + r1.width)) {
        return false;
    }
    // if one rectangle is above other
    // noinspection RedundantIfStatementJS
    if (r1.top >= (r2.top + r2.height) || r2.top >= (r1.top + r1.height)) {
        return false;
    }
    return true;
}

function searchForName(node: HierarchyEntity, name: string): boolean {
    const lcName = name.toLowerCase();

    function _check(n: HierarchyEntity): boolean {
        return n.name.toLowerCase().includes(lcName) || !!(n.children || []).find(c => _check(c));
    }

    return _check(node);
}

function mkEnergyCommunityTreeData(entities: HierarchyEntity[]): HierarchyEntity[] {
    const rootNodes: HierarchyEntity[] = [];
    const energyCommunities: HierarchyEntity[] = [];
    entities.forEach(entity => {
        if (entity.type === EntityType.ENERGY_COMMUNITY) {
            energyCommunities.push({...entity, children: []}); // hide community's children
        } else if (entity.type === EntityType.ROOT_NODE) {
            const rootNodeEnergyCommunities: HierarchyEntity[] = [];
            (entity.children || []).forEach(rootNodeChild => {
                if (rootNodeChild.type === EntityType.ENERGY_COMMUNITY) {
                    rootNodeEnergyCommunities.push({...rootNodeChild, children: []}); // hide community's children
                }
            });
            rootNodes.push({...entity, children: rootNodeEnergyCommunities}); // only keep child communities
        }
    });
    return [
        ...rootNodes,
        ...energyCommunities
    ];
}
